import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Spinner, TextInput, Textarea } from "flowbite-react";
import { HiArrowLeft, HiArrowRight, HiCheck, HiOutlinePhotograph } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getLoja, atualizarLoja as atualizarLojaApi } from "../../api/user";
import { criarProduto as criarProdutoApi } from "../../api/produto";
import { uploadFotos as uploadFotosApi } from "../../api/fotos";
import { LOAD_LOJA_SUCCESS, UPDATE_LOJA_SUCCESS } from "../../actions/user";
import { CREATE_PRODUTO_SUCCESS } from "../../actions/produto";
import { UPLOAD_FOTOS_SUCCESS } from "../../actions/foto";
import { showNotification } from "../../actions/notification";
import { getStoreProfilePictureUrl } from "../../utils/storeProfilePicture";

const ONBOARDING_DONE_KEY = "vintree_onboarding_done";

const etapas = [
  { id: "foto", titulo: "Foto de perfil" },
  { id: "tagline", titulo: "Tagline" },
  { id: "whatsapp", titulo: "WhatsApp" },
  { id: "produto", titulo: "Primeiro produto" },
  { id: "fotos", titulo: "Fotos" },
];

const marcarOnboardingConcluido = () => {
  window.localStorage.setItem(ONBOARDING_DONE_KEY, "1");
};

const normalizarWhatsapp = (value) => String(value || "").replace(/\D/g, "");

const validarWhatsapp = (value) => {
  const digits = normalizarWhatsapp(value);
  if (/^55\d{10,11}$/.test(digits)) return digits;
  if (/^\d{10,11}$/.test(digits)) return digits;
  return null;
};

const OnboardingWizard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lojaAtual = useSelector((state) => state.user.loja);

  const [loja, setLoja] = useState(lojaAtual);
  const [etapaAtual, setEtapaAtual] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [carregandoLoja, setCarregandoLoja] = useState(!lojaAtual);
  const [erro, setErro] = useState("");
  const [produtoCriado, setProdutoCriado] = useState(null);
  const [fotoPerfil, setFotoPerfil] = useState(null);
  const [tagline, setTagline] = useState(lojaAtual?.slogan || "");
  const [whatsapp, setWhatsapp] = useState(lojaAtual?.whatsapp || "");
  const [produtoForm, setProdutoForm] = useState({
    referencia: "",
    descricao: "",
    marca: "",
  });
  const [fotos, setFotos] = useState([]);
  const [fotoPreviews, setFotoPreviews] = useState([]);

  useEffect(() => {
    if (window.localStorage.getItem(ONBOARDING_DONE_KEY) === "1") {
      navigate("/app/home", { replace: true });
      return;
    }

    if (lojaAtual) {
      setLoja(lojaAtual);
      setTagline((prev) => prev || lojaAtual.slogan || "");
      setWhatsapp((prev) => prev || lojaAtual.whatsapp || "");
      setCarregandoLoja(false);
      return;
    }

    let ativo = true;
    setCarregandoLoja(true);
    getLoja()
      .then(({ data }) => {
        if (!ativo) return;
        setLoja(data);
        setTagline((prev) => prev || data?.slogan || "");
        setWhatsapp((prev) => prev || data?.whatsapp || "");
        dispatch({ type: LOAD_LOJA_SUCCESS, payload: data });
      })
      .catch((error) => {
        if (!ativo) return;
        setErro(error.message || "Nao foi possivel carregar sua loja.");
      })
      .finally(() => {
        if (ativo) setCarregandoLoja(false);
      });

    return () => {
      ativo = false;
    };
  }, [dispatch, lojaAtual, navigate]);

  const previewFotoPerfil = useMemo(() => {
    if (fotoPerfil instanceof Blob) {
      return URL.createObjectURL(fotoPerfil);
    }
    return getStoreProfilePictureUrl(loja?.profile_picture, loja?.updatedAt || loja?.updated_at);
  }, [fotoPerfil, loja?.profile_picture, loja?.updatedAt, loja?.updated_at]);

  useEffect(() => {
    if (!(fotoPerfil instanceof Blob)) return undefined;
    return () => URL.revokeObjectURL(previewFotoPerfil);
  }, [fotoPerfil, previewFotoPerfil]);

  useEffect(() => {
    const previews = fotos.map((foto) => ({
      key: `${foto.name}-${foto.lastModified}`,
      nome: foto.name,
      url: URL.createObjectURL(foto),
    }));
    setFotoPreviews(previews);

    return () => {
      previews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [fotos]);

  const progresso = ((etapaAtual + 1) / etapas.length) * 100;
  const etapa = etapas[etapaAtual];

  const finalizar = () => {
    marcarOnboardingConcluido();
    navigate("/app/home", { replace: true });
  };

  const salvarLoja = async (payload) => {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });

    const { data } = await atualizarLojaApi(formData);
    setLoja(data);
    dispatch({ type: UPDATE_LOJA_SUCCESS, payload: data });
  };

  const salvarProduto = async () => {
    if (produtoCriado) return produtoCriado;

    if (!loja?.id) {
      throw new Error("A loja ainda esta carregando.");
    }

    const incompleto = ["referencia", "descricao", "marca"].some(
      (campo) => !produtoForm[campo].trim()
    );
    if (incompleto) {
      throw new Error("Preencha referencia, descricao e marca.");
    }

    const { data } = await criarProdutoApi({
      ...produtoForm,
      lojaId: loja.id,
      tags: [],
    });

    setProdutoCriado(data);
    dispatch({ type: CREATE_PRODUTO_SUCCESS, payload: data });
    return data;
  };

  const salvarFotos = async () => {
    if (fotos.length === 0) return;
    const produto = await salvarProduto();
    const { data } = await uploadFotosApi(produto.id, produto.descricao, fotos);
    dispatch({ type: UPLOAD_FOTOS_SUCCESS, payload: data });
  };

  const avancar = async () => {
    setErro("");
    setSalvando(true);

    try {
      if (etapa.id === "foto" && fotoPerfil) {
        await salvarLoja({ profile_picture: fotoPerfil });
      }

      if (etapa.id === "tagline") {
        await salvarLoja({ slogan: tagline });
      }

      if (etapa.id === "whatsapp") {
        const whatsappNormalizado = validarWhatsapp(whatsapp);
        if (!whatsappNormalizado) {
          throw new Error("Digite um WhatsApp valido com DDD, com 10 ou 11 digitos.");
        }
        await salvarLoja({ whatsapp: whatsappNormalizado });
        setWhatsapp(whatsappNormalizado);
      }

      if (etapa.id === "produto") {
        await salvarProduto();
      }

      if (etapa.id === "fotos") {
        await salvarFotos();
        dispatch(
          showNotification({
            message: "Primeiros passos salvos.",
            color: "success",
          })
        );
        finalizar();
        return;
      }

      setEtapaAtual((prev) => Math.min(prev + 1, etapas.length - 1));
    } catch (error) {
      setErro(error.message || "Nao foi possivel salvar esta etapa.");
    } finally {
      setSalvando(false);
    }
  };

  const voltar = () => {
    setErro("");
    setEtapaAtual((prev) => Math.max(prev - 1, 0));
  };

  const pular = () => {
    if (etapa.id === "produto" || etapa.id === "fotos") {
      finalizar();
      return;
    }

    setErro("");
    setEtapaAtual((prev) => Math.min(prev + 1, etapas.length - 1));
  };

  const handleFotoPerfil = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErro("Selecione uma imagem.");
      return;
    }
    setFotoPerfil(file);
  };

  const handleFotosProduto = (event) => {
    const arquivos = Array.from(event.target.files || []).filter((file) =>
      file.type.startsWith("image/")
    );
    setFotos(arquivos);
  };

  if (carregandoLoja) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto bg-gradient-to-br from-[#fff1be] via-[#f7e8f3] to-[#dac3ff] px-4 py-6 sm:px-6">
      <div className="mx-auto flex min-h-full max-w-3xl items-center">
        <div className="w-full rounded-lg border border-gray-200 bg-white p-6 shadow-xl sm:p-8">
          <div className="mb-7">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>{etapa.titulo}</span>
              <span>
                {etapaAtual + 1}/{etapas.length}
              </span>
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-gray-200">
              <div
                className="h-1.5 rounded-full bg-purple-600 transition-all"
                style={{ width: `${progresso}%` }}
              />
            </div>
          </div>

          {erro ? (
            <Alert color="failure" className="mb-5">
              {erro}
            </Alert>
          ) : null}

          {etapa.id === "foto" ? (
            <div className="space-y-6">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Foto da loja</h1>
                <p className="mt-1 text-sm text-gray-500">Escolha uma imagem de perfil.</p>
              </div>

              <div className="flex flex-col items-center gap-5 sm:flex-row">
                <img
                  src={previewFotoPerfil}
                  alt="Foto da loja"
                  className="h-28 w-28 rounded-full border border-gray-200 object-cover"
                />
                <div className="w-full">
                  <input
                    id="foto-perfil"
                    type="file"
                    accept="image/*"
                    onChange={handleFotoPerfil}
                    className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-900 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white"
                  />
                </div>
              </div>
            </div>
          ) : null}

          {etapa.id === "tagline" ? (
            <div className="space-y-6">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Tagline</h1>
                <p className="mt-1 text-sm text-gray-500">Uma frase curta para sua vitrine.</p>
              </div>

              <div>
                <TextInput
                  id="tagline"
                  value={tagline}
                  onChange={(event) => setTagline(event.target.value)}
                  placeholder="Seu estilo em um só lugar"
                />
              </div>
            </div>
          ) : null}

          {etapa.id === "whatsapp" ? (
            <div className="space-y-6">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">WhatsApp</h1>
                <p className="mt-1 text-sm text-gray-500">O contato principal da loja.</p>
              </div>

              <div>
                <TextInput
                  id="whatsapp"
                  type="tel"
                  inputMode="numeric"
                  value={whatsapp}
                  onChange={(event) => setWhatsapp(normalizarWhatsapp(event.target.value))}
                  maxLength={13}
                  placeholder="1133334444 ou 11999999999"
                />
              </div>
            </div>
          ) : null}

          {etapa.id === "produto" ? (
            <div className="space-y-6">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Primeiro produto</h1>
                <p className="mt-1 text-sm text-gray-500">Referencia, descricao e marca.</p>
              </div>

              <div className="grid gap-4">
                <div>
                  <TextInput
                    id="referencia"
                    value={produtoForm.referencia}
                    onChange={(event) =>
                      setProdutoForm((prev) => ({ ...prev, referencia: event.target.value }))
                    }
                    placeholder="Ex: C7428926"
                  />
                </div>

                <div>
                  <Textarea
                    id="descricao"
                    rows={3}
                    value={produtoForm.descricao}
                    onChange={(event) =>
                      setProdutoForm((prev) => ({ ...prev, descricao: event.target.value }))
                    }
                    placeholder="Ex: Biquini Agua de Coco"
                  />
                </div>

                <div>
                  <TextInput
                    id="marca"
                    value={produtoForm.marca}
                    onChange={(event) =>
                      setProdutoForm((prev) => ({ ...prev, marca: event.target.value }))
                    }
                    placeholder="Ex: Agua de Coco"
                  />
                </div>
              </div>
            </div>
          ) : null}

          {etapa.id === "fotos" ? (
            <div className="space-y-6">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Fotos do produto</h1>
                <p className="mt-1 text-sm text-gray-500">Selecione uma ou mais fotos.</p>
              </div>

              <label
                htmlFor="fotos-produto"
                className="flex min-h-48 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 text-center transition hover:border-purple-400 hover:bg-purple-50"
              >
                <HiOutlinePhotograph className="h-9 w-9 text-purple-600" />
                <span className="mt-3 text-sm font-medium text-gray-800">
                  {fotos.length
                    ? `${fotos.length} foto(s) selecionada(s)`
                    : "Selecionar fotos"}
                </span>
                <input
                  id="fotos-produto"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFotosProduto}
                  className="sr-only"
                />
              </label>

              {fotoPreviews.length ? (
                <div className="grid grid-cols-3 gap-3">
                  {fotoPreviews.slice(0, 6).map((foto) => (
                    <img
                      key={foto.key}
                      src={foto.url}
                      alt={foto.nome}
                      className="aspect-square rounded-lg object-cover"
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Button color="light" onClick={pular} disabled={salvando}>
              Pular por enquanto
            </Button>

            <div className="flex gap-3">
              <Button color="light" onClick={voltar} disabled={salvando || etapaAtual === 0}>
                <HiArrowLeft className="mr-2 h-4 w-4" />
                Voltar
              </Button>
              <Button gradientDuoTone="purpleToPink" onClick={avancar} disabled={salvando}>
                {salvando ? (
                  <Spinner size="sm" className="mr-2" />
                ) : etapaAtual === etapas.length - 1 ? (
                  <HiCheck className="mr-2 h-4 w-4" />
                ) : (
                  <HiArrowRight className="mr-2 h-4 w-4" />
                )}
                {etapaAtual === etapas.length - 1 ? "Concluir" : "Avancar"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
